import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { HOME_VIEW_AS_QUERY_PARAM, HomeViewAs } from "@core/constants";
import { Sort } from "@models";
import useQueryState from "@hooks/useQueryState";
import { Metric } from "@models/Metric";
import { HomeAiSearchResponseModel } from "@api/useAiSearchSettingsAndAnswer";

type SortContext = {
    sortKey: string;
    setSortKey: Dispatch<SetStateAction<string>>;
    sortDirection: Sort;
    setSortDirection: Dispatch<SetStateAction<Sort>>;
}

type HomeContextData = {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    viewAs: HomeViewAs;
    setViewAs: (value: HomeViewAs) => void;
    sortContext: SortContext;
    setSortContext: Dispatch<SetStateAction<SortContext>>
    resultCount: number;
    setResultCount: Dispatch<SetStateAction<number>>;
}

const DEFAULTS: HomeContextData = {
    search: '',
    setSearch: () => null,
    viewAs: HomeViewAs.Tile,
    setViewAs: () => null,
    sortContext: {
        sortKey: '',
        setSortKey: () => null,
        sortDirection: Sort.Asc,
        setSortDirection: () => null,
    },
    setSortContext: () => null,
    resultCount: 0,
    setResultCount: () => null,
}

const HomeContext = createContext<HomeContextData>(DEFAULTS);

export function useHomeContext() {
    return useContext(HomeContext);
}

export const HomeContextProvider: FCX = ({
    children,
}) => {
    const [search, setSearch] = useState(DEFAULTS.search);
    const [viewAs, setViewAs] = useQueryState<HomeContextData['viewAs']>(HOME_VIEW_AS_QUERY_PARAM, DEFAULTS.viewAs);
    const [sortContext, setSortContext] = useState<HomeContextData['sortContext']>(DEFAULTS.sortContext);
    const [resultCount, setResultCount] = useState<HomeContextData['resultCount']>(DEFAULTS.resultCount);


    const contextValue = useMemo(
        () => ({
            search,
            setSearch,
            viewAs,
            setViewAs,
            sortContext,
            setSortContext,
            resultCount,
            setResultCount,
        }),
        [search, viewAs, setViewAs, sortContext, resultCount]
    );

    return (
        <HomeContext.Provider value={contextValue}>
            {children}
        </HomeContext.Provider>
    );
};

export type MetricContextData = {
    displayMetricId: Metric | null;
    setDisplayMetricId: Dispatch<SetStateAction<Metric | null>>;
}

export const METRIC_CONTEXT_DATA_DEFAULTS: MetricContextData = {
    displayMetricId: Metric.KnowIt,
    setDisplayMetricId: () => null,
}

const MetricContext = createContext<MetricContextData>(METRIC_CONTEXT_DATA_DEFAULTS);

export function useDisplayMetricContext() {
    return useContext(MetricContext);
}

export const MetricContextProvider: FCX = ({
    children,
}) => {
    const [displayMetricId, setDisplayMetricId] = useState<MetricContextData['displayMetricId']>(METRIC_CONTEXT_DATA_DEFAULTS.displayMetricId);

    const contextValue = useMemo(
        () => ({
            displayMetricId,
            setDisplayMetricId,
        }),
        [displayMetricId, setDisplayMetricId]
    )

    return (
        <MetricContext.Provider value={contextValue}>
            {children}
        </MetricContext.Provider>
    );
};

type HomeSearchSettingsContextData = {
    isLoaded: boolean;
    data: HomeAiSearchResponseModel['settings'];
    resetSearch: () => void;
}

const HomeSearchSettingsContext = createContext<HomeSearchSettingsContextData>({
    isLoaded: false,
    data: null,
    resetSearch: () => null,
});

export const HomeSearchSettingsContextProvider: FCX<{
    data: HomeSearchSettingsContextData['data'],
    isLoaded: HomeSearchSettingsContextData['isLoaded'],
    setIsSearchCancelled: (value: boolean) => void,
}> = ({
    data,
    isLoaded,
    setIsSearchCancelled,
    children
}) => {
    const resetSearch = useCallback(
        () => {
            setIsSearchCancelled(true);
        },
        [setIsSearchCancelled]
    );

    const value = useMemo(
        (): HomeSearchSettingsContextData => ({
            isLoaded,
            data,
            resetSearch,
        }),
        [data, resetSearch, isLoaded]
    );

    return (
        <HomeSearchSettingsContext.Provider value={value}>
            {children}
        </HomeSearchSettingsContext.Provider>
    );
}

export function useHomeSearchSettingsContext() {
    return useContext(HomeSearchSettingsContext);
}